import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { StepContainer, StepContent, StepDescription, StepHeading } from '../../components';
import { useActionItem, useIsMobile, useL10n, useStore } from '@uplift/hooks';
import { AsyncButton, Button, Loader, PhoneVerificationModal, StickyMobileFooter } from '@uplift/ui-v2';
import { useCurrentuser, useCurrentuserRefetch } from '@uplift/contexts';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { ROUTING_CONFIG } from '@uplift/constants';
import { ActionItemKeys } from '@uplift/api';
import { PolicyDisclaimers } from '../../../components/crisis-hotline-footer/policy-disclaimer';
import { ArrowForwardSharp } from '@mui/icons-material';

export type BookingSuccessProps = {
  onExit: () => void;
  intakeFormSubmissionId: string;
};

export type NextButtonProps = BookingSuccessProps & {
  handleModalOpen: () => void;
  goTo: (link: string) => Promise<void>;
};

type ButtonWrapperProps = {
  children: React.ReactNode;
};

const NextButton: React.VFC<NextButtonProps> = ({ intakeFormSubmissionId, goTo, handleModalOpen }) => {
  const formProgressData = useActionItem(ActionItemKeys.CLIENT_FORM_PROGRESS);
  const l10n = useL10n();

  if (formProgressData.isLoading) {
    return (
      <Loader
        color="secondary"
        size="20px"
        height="38px"
      />
    );
  }

  if (intakeFormSubmissionId) {
    return (
      <Button
        variant="contained"
        color="secondary"
        endIcon={<ArrowForwardSharp />}
        onClick={handleModalOpen}
      >
        {l10n.matching.firstSessionNew.verifyPhone}
      </Button>
    );
  }

  return (
    <AsyncButton
      color="secondary"
      variant="contained"
      onClick={async () => {
        await goTo(ROUTING_CONFIG.main);
      }}
    >
      {l10n.matching.firstSession.goToDashbaord}
    </AsyncButton>
  );
};

const ButtonWrapper: React.VFC<ButtonWrapperProps> = ({ children }) => {
  const isMobile = useIsMobile('sm');

  if (isMobile) {
    return (
      <StickyMobileFooter>
        {children}
      </StickyMobileFooter>
    );
  }

  return (
    <Box mt={8}>
      {children}
    </Box>
  );

};

export const BookingSuccess: React.VFC<BookingSuccessProps> = props => {
  const { classes } = useBookingSuccessStyles();
  const l10n = useL10n();
  const [ verificationModalOpened, setVerificationModalOpened ] = useState(false);
  const clientSMS = useActionItem(ActionItemKeys.CLIENT_SMS_VERIFY);
  const me = useCurrentuser();
  const history = useHistory();
  const { authStore } = useStore();
  const { refetchUser } = useCurrentuserRefetch();
  const isMobile = useIsMobile('sm');
  const goTo = async (link: string) => {
    await refetchUser();
    await authStore.fetchAccessTokenByRefreshToken();

    props.onExit();
    history.push(link);
  };

  const handleModalOpen = () => {
    setVerificationModalOpened(true);
  };

  const handleSubmit = useCallback(() => {
    goTo(`/form/submission/${props.intakeFormSubmissionId}`);
  }, [ props.intakeFormSubmissionId ]);


  return (
    <StepContainer>
      <StepContent className={classes.content}>
        <div className={classes.imageContainer}>
          <img
            width="200px"
            src="https://scdn.joinuplift.co/public/application-assets/puzzle.png"
            alt="Hands"
          />
        </div>
        <StepHeading className={classes.heading}>
          You reached out for help, and that’s something to be proud of
        </StepHeading>
        <StepDescription className={classes.description}>
          Answer a few questions to help your provider get to know you. The more you feel comfortable sharing, the more your provider can tailor your first session to your needs.
        </StepDescription>
        <Box mt={2}>
          <Typography variant="h3" style={{ fontSize: '16px' }}>
            {l10n.matching.firstSessionNew.letsStart}
          </Typography>
        </Box>
        <ButtonWrapper>


          <NextButton
            intakeFormSubmissionId={props.intakeFormSubmissionId}
            onExit={props.onExit}
            handleModalOpen={handleModalOpen}
            goTo={goTo}
          />
        </ButtonWrapper>

        <PhoneVerificationModal
          open={verificationModalOpened}
          userId={me?.id}
          phone={clientSMS.contact?.value}
          contactId={clientSMS.contact?.contactId}
          contactName={clientSMS.contact?.contactName}
          onSubmit={handleSubmit}
          onClose={() => setVerificationModalOpened(false)}
          showAppointmentSmsOptIn
        />
      </StepContent>

      {!isMobile && (
        <Box pl={3} pb={2}>
          <PolicyDisclaimers />
        </Box>
      )}

    </StepContainer>
  );
};

const useBookingSuccessStyles = makeStyles({ name: 'BookingSuccess' })(theme => ({
  /**
   * wrap image in fixed height container so that it doesn't jump around when loading
   */
  imageContainer: {
    height: '215px',
    margin: '0 auto',
    width: 'fit-content'
  },
  content: {
    paddingTop: 0,
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

  },
  description: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  },
  heading: {
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  }
}));
